import type { Component, AsyncComponentLoader } from 'vue';
import { defineAsyncComponent, markRaw }        from 'vue';
import DialogsService                           from './DialogsService';
import Utils                                    from "@/core/utils/Utils";
import DialogsRouter                            from "@/core/services/Dialogs/DialogsRouter";

/**
 * Загрузчик компонента диалога (тип Vue).
 * Динамический import рвёт статический граф зависимостей реестра:
 * DialogsRegistry больше не импортирует .vue напрямую, что устраняет
 * циклические импорты и full-reload HMR.
 *
 * Используется AsyncComponentLoader из Vue, а не собственный тип:
 * import('*.vue') возвращает конкретный тип SFC, который не подходит
 * под узкое () => Promise<{ default: Component }>, но подходит под широкий
 * AsyncComponentLoader.
 */

/**
 * Класс диалога.
 * Оборачивает компонент и предоставляет методы управления.
 *
 * Компонент загружается лениво через defineAsyncComponent при первом обращении.
 * Имя задаётся явно, так как до загрузки модуля вытащить его неоткуда.
 *
 * Поддерживает автоматическую синхронизацию с URL через DialogsRouter.
 * Для этого нужно указать routeEntityName — имя сущности для роута.
 * Имя приводится к camelCase и попадает в URL (?dialog=assembly:123).
 *
 * @example
 * // Простой диалог без роутинга
 * new Dialog('VConfirmDialog', () => import('.../VConfirmDialog.vue'))
 *
 * // Диалог с роутингом (?dialog=paymentRequest:123)
 * new Dialog('PaymentRequestCard', () => import('.../PaymentRequestCard.vue'), 'paymentRequest')
 */
export default class Dialog {
    readonly name: string;

    /** Имя сущности для синхронизации с URL, в camelCase (например 'paymentRequest') */
    readonly routeEntityName: string | null;

    /** Загрузчик компонента */
    private readonly loader: AsyncComponentLoader;

    /** Лениво созданный async-компонент (создаётся один раз при первом обращении) */
    private cachedComponent: Component | null = null;

    /**
     * Резолвер активного showAsPromise. null — диалог открыт без промиса
     * либо промис уже разрешён. Один одновременный showAsPromise на инстанс:
     * диалоги одноэкземплярные, повторный show перезапишет поле новым резолвером.
     */
    private promiseResolve: ((result: boolean) => void) | null = null;

    constructor(
        name: string,
        loader: AsyncComponentLoader,
        routeEntityName: string | null = null,
    ) {
        if (!name) {
            throw new Error('Компонент диалога должен иметь имя');
        }
        this.name = name;
        this.loader = loader;

        if (routeEntityName !== null) {
            // Нормализуем безусловно: для camelCase-строк camelize — no-op,
            // snake_case/kebab-case/пробелы тихо приводятся к camelCase.
            // Пустой результат (вырожденный вход вроде '---') — диалог без роутинга.
            const normalized = Utils.string.camelize(routeEntityName);

            if (Utils.string.isNotEmptyString(normalized)) {
                this.routeEntityName = normalized;
            } else {
                this.routeEntityName = null;
            }
        } else {
            this.routeEntityName = null;
        }
    }

    /**
     * Async-компонент для рендеринга в DialogsContainer.
     * Создаётся лениво один раз и кэшируется.
     */
    get component(): Component {
        if (this.cachedComponent === null) {
            this.cachedComponent = markRaw(defineAsyncComponent(this.loader));
        } else {
            // компонент уже создан — используем кэш
        }

        return this.cachedComponent;
    }

    show(
        componentProps: Record<string, any> = {},
        eventHandlers: Record<string, (data?: any) => void> = {},
        contentSlots: Record<string, any> | string | null = null,
    ): string {
        if (this.routeEntityName !== null) {
            const entityName = this.routeEntityName;
            const entityId = this.resolveEntityId(componentProps);

            if (entityId !== null) {
                DialogsRouter.push(entityName, entityId);
            } else {
                // id не определён — диалог открываем без синхронизации с URL
            }
        } else {
            // диалог без роутинга
        }

        return DialogsService.show(this, componentProps, eventHandlers, contentSlots);
    }

    /**
     * Как show, но возвращает промис, резолвящийся при закрытии диалога:
     *
     * true  — диалог завершён успешно через resolve() (например onSubmit карточки);
     * false — закрыт любым другим способом (крестик, оверлей, Escape, отмена,
     *         программный hide()).
     *
     * Позволяет вызывающему коду различать исход, не зная механики промисов:
     *
     *     if (await SomeCard.showAsPromise({ ... })) {
     *         this.loader.refresh();
     *     }
     *
     * Старые вызовы `await showAsPromise(); refresh()` продолжают работать —
     * промис резолвится при любом закрытии, значение игнорируется.
     *
     * Отмена резолвится по close — его эмитит VDialogV3 на всех пользовательских
     * путях закрытия. Пользовательский close, если передан, вызывается перед
     * резолвом. Роутинг и прочая обвязка наследуются от show — здесь не дублируются.
     */
    showAsPromise(
        componentProps: Record<string, any> = {},
        eventHandlers: Record<string, (data?: any) => void> = {},
        contentSlots: Record<string, any> | string | null = null,
    ): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.promiseResolve = resolve;

            const originalOnClose = eventHandlers.close;

            const handlers = {
                ...eventHandlers,
                close: (data?: any) => {
                    if (originalOnClose) {
                        originalOnClose(data);
                    }

                    if (this.promiseResolve !== null) {
                        // Закрытие без resolve() — отмена
                        this.promiseResolve(false);
                        this.promiseResolve = null;
                    } else {
                        // Промис уже разрешён через resolve()
                    }

                    /**
                     * ИЗВЕСТНАЯ ПРОБЛЕМА: две карточки одного вида (номенклатура поверх
                     * номенклатуры), обе открыты через showAsPromise. Крестик на верхней
                     * вызывает VDialogV3.close():
                     *
                     *     this.$emit('close');                              // 1
                     *     DialogsService.hideIfOpen(this.parentDialogName); // 2
                     *
                     * 1 — срабатывает эта обёртка, ниже hideIfOpen(this) закрывает по имени
                     *     последний открытый диалог этого вида — верхнюю карточку.
                     * 2 — снова по имени последний открытый, теперь это нижняя карточка —
                     *     закрывается и она.
                     *
                     * Из-за этого строка была закомментирована в 1f2f1900e.
                     * Как правильно — пока не решено.
                     */
                    DialogsService.hideIfOpen(this);
                },
            };

            this.show(componentProps, handlers, contentSlots);
        });
    }

    /**
     * Успешное завершение диалога: резолвит промис showAsPromise в true
     * и закрывает диалог. Вызывается карточкой из onSubmit вместо hide().
     *
     * Если диалог открыт через show (без промиса) — просто закрывает.
     */
    resolve(): void {
        if (this.promiseResolve !== null) {
            this.promiseResolve(true);
            this.promiseResolve = null;
        } else {
            // открыт без промиса — резолвить нечего
        }

        this.hide();
    }

    hide(): void {
        DialogsService.hide(this);

        // Программное закрытие извне (например, гасим карточку поверх) при висящем
        // showAsPromise — это отмена: эмита close не будет, резолвим здесь
        if (this.promiseResolve !== null) {
            this.promiseResolve(false);
            this.promiseResolve = null;
        } else {
            // промиса нет или уже разрешён
        }
    }

    isOpen(): boolean {
        return DialogsService.isOpen(this);
    }

    hideIfOpen(): void {
        if (this.isOpen()) {
            this.hide();
        } else {
            // диалог не открыт — закрывать нечего
        }
    }

    private resolveEntityId(componentProps: Record<string, any>): number | null {
        // По соглашению карточки, открываемые по ссылке, всегда принимают пропс id
        if (typeof componentProps['id'] === 'number') {
            return componentProps['id'];
        } else {
            return null;
        }
    }
}
